'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import LinkIcon from '@/components/icons/LinkIcon';
import { type ColumnDef, type Row } from '@tanstack/react-table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { WebinarSessionDetail } from '@/features/webinar/types';
import IconNote from '@/components/icons/IconNote';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { FlaskConical, Play, UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';

export type WebinarListItem = {
  id: string;
  title: string;
  type: string;
  date: string;
  attendee: number;
  moderator: { fullname: string; job: string }[];
  status: string;
  webinarId: number;
  sessionId: number;
  nextSessionId?: number;
  registrationUrl?: string;
  sessionDetail?: WebinarSessionDetail | null;
  accessCodeEnabled?: boolean;
  evergreenTestAvailable?: boolean;
};

function AccessCell({ row }: { row: Row<WebinarListItem> }) {
  const { nextSessionId, sessionDetail, accessCodeEnabled } = row.original;
  const { t } = useTranslation('webinars');
  const currentWorkspace = useAppSelector((s) => s.workspace.currentWorkspace);
  // Rôle brut : le back n'ouvre le test qu'aux ADMIN, pas aux anciens USER affichés comme tels.
  const testWorkspaceId =
    row.original.type === 'EVERGREEN' &&
    row.original.evergreenTestAvailable === true &&
    currentWorkspace?.currentUserRole === 'ADMIN'
      ? currentWorkspace.id
      : null;
  // Indépendant des liens d'accès : un Evergreen publié sans prochaine session reste testable.
  const testButton =
    testWorkspaceId != null ? (
      <Button
        onClick={(e) => {
          e.stopPropagation();
          window.open(
            `/evergreen-test/${testWorkspaceId}/${row.original.webinarId}`,
            '_blank',
            'noopener,noreferrer',
          );
        }}
        variant="outline"
        className="text-sm"
      >
        <FlaskConical size={11} />
        {t('access.testEvergreen')}
      </Button>
    ) : null;
  if (!nextSessionId && !sessionDetail?.replayRegistrationUrl) {
    if (!testButton) return <div className="text-sm text-secondary">{t('access.empty')}</div>;
    return (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {testButton}
        <div className="text-sm text-secondary">{t('access.empty')}</div>
      </div>
    );
  }

  const people: {
    accessCode?: string;
    fullname: string;
    job: string;
    pictureUrl?: string;
    accessUrl?: string;
    userId?: string;
    role: string;
  }[] = [];
  if (sessionDetail?.presenters?.length) {
    sessionDetail.presenters.forEach((p) =>
      people.push({ ...p, role: t('speakersForm.presenter') }),
    );
  }
  if (sessionDetail?.speakers?.length) {
    sessionDetail.speakers.forEach((p) => people.push({ ...p, role: t('speakersForm.guest') }));
  }
  if (sessionDetail?.moderators?.length) {
    sessionDetail.moderators.forEach((p) =>
      people.push({ ...p, role: t('speakersForm.moderator') }),
    );
  }

  const visiblePeople =
    row.original.type === 'EVERGREEN'
      ? people.filter((p) => p.role === t('speakersForm.moderator'))
      : people;

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      {nextSessionId && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            window.open(
              `/admin-live/${row.original.webinarId}/${row.original.sessionId}`,
              '_blank',
              'noopener,noreferrer',
            );
          }}
          variant="outline"
          className="text-sm"
        >
          <Play size={11} className="" />
          {row.original.type === 'EVERGREEN' ? t('access.launchEvergreen') : t('access.launchLive')}
        </Button>
      )}
      {testButton}
      <Dialog>
        <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" className="text-sm">
            <LinkIcon size={20} color="currentColor" />
            {t('access.links')}
          </Button>
        </DialogTrigger>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{t('access.linksTitle')}</DialogTitle>
            <DialogDescription>{t('access.linksDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-subtitle rounded-[20px]">
              <div className="flex justify-between flex-col gap-6 w-full">
                {nextSessionId && (
                  <div className="flex gap-2 items-start justify-between w-full">
                    <div className="flex gap-2 items-start">
                      <Avatar>
                        <AvatarFallback>
                          <UserIcon size={16} />
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-base font-medium text-black">
                          {t('access.liveRegistrationLink')}
                        </p>
                        <a
                          href={row.original.registrationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary underline underline-offset-2 hover:text-primary-700 break-all min-w-0 flex-1 line-clamp-1"
                        >
                          {`${row.original.registrationUrl}`}
                        </a>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(`${row.original.registrationUrl}`);
                        toast.success(t('access.copySuccess'));
                      }}
                      title={t('access.copy')}
                    >
                      <IconNote size={24} color="#2c6cff" />
                    </button>
                  </div>
                )}
                {sessionDetail?.replayRegistrationUrl && (
                  <div className="flex gap-2 items-start justify-between w-full">
                    <div className="flex gap-2 items-start">
                      <Avatar>
                        <AvatarFallback>
                          <UserIcon size={16} />
                        </AvatarFallback>
                      </Avatar>

                      <div>
                        <p className="text-base font-medium text-black">
                          {t('access.replayRegistrationLink')}
                        </p>
                        <a
                          href={sessionDetail.replayRegistrationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary underline underline-offset-2 hover:text-primary-700 break-all min-w-0 flex-1 line-clamp-1"
                        >
                          {`${sessionDetail?.replayRegistrationUrl}`}
                        </a>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(`${sessionDetail?.replayRegistrationUrl}`);
                        toast.success(t('access.copySuccess'));
                      }}
                      title={t('access.copy')}
                    >
                      <IconNote size={24} color="#2c6cff" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <Separator />
            {visiblePeople.length > 0 ? (
              <Accordion
                type="single"
                collapsible
                className="w-full bg-white rounded-[20px] space-y-4"
                defaultValue={
                  visiblePeople[0].userId ??
                  visiblePeople[0].accessUrl ??
                  `${visiblePeople[0].role}-${visiblePeople[0].fullname}-0`
                }
              >
                {visiblePeople.map((person, index) => {
                  const personKey =
                    person.userId ??
                    person.accessUrl ??
                    `${person.role}-${person.fullname}-${index}`;
                  return (
                    <AccordionItem key={personKey} value={personKey} className="bg-subtitle">
                      <AccordionTrigger>
                        <div className="flex gap-2 items-center">
                          <Avatar>
                            {person.pictureUrl ? (
                              <AvatarImage src={person.pictureUrl} alt={person.fullname} />
                            ) : null}
                            <AvatarFallback>{person.fullname.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <p className="text-base font-medium text-black">{person.fullname}</p>
                          <Badge
                            variant={
                              person.role === t('speakersForm.presenter')
                                ? 'live'
                                : person.role === t('speakersForm.moderator')
                                  ? 'evergreen'
                                  : 'finished'
                            }
                          >
                            {person.role}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {person.accessUrl ? (
                          <>
                            <div className="flex items-center justify-between gap-2 w-full">
                              <p className="text-sm text-black font-medium">
                                {t('access.accessLink')}
                              </p>
                              {person.role === t('speakersForm.guest') ? (
                                <p className="text-sm text-secondary break-all min-w-0 flex-1 line-clamp-1">
                                  {person.accessUrl}
                                </p>
                              ) : (
                                <a
                                  href={person.accessUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary underline underline-offset-2 hover:text-primary-700 break-all min-w-0 flex-1 line-clamp-1"
                                >
                                  {person.accessUrl}
                                </a>
                              )}
                              <button
                                type="button"
                                className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(person.accessUrl ?? '');
                                  toast.success(t('access.copySuccess'));
                                }}
                                title={t('access.copy')}
                              >
                                <IconNote size={24} color="#2c6cff" />
                              </button>
                            </div>
                            {accessCodeEnabled &&
                              person?.accessCode &&
                              person.role === t('speakersForm.guest') && (
                                <div className="flex items-center justify-between gap-2 w-full">
                                  <p className="text-sm text-black font-medium">
                                    {t('access.accessCode')} :
                                  </p>
                                  <p className="text-sm text-secondary break-all min-w-0 flex-1 line-clamp-1">
                                    {person.accessCode}
                                  </p>
                                  <button
                                    type="button"
                                    className="shrink-0 p-1 rounded  transition-colors cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(person.accessCode ?? '');
                                      toast.success(t('access.copyCodeSuccess'));
                                    }}
                                    title={t('access.copyCode')}
                                  >
                                    <IconNote size={24} color="#2c6cff" />
                                  </button>
                                </div>
                              )}
                          </>
                        ) : (
                          <p className="text-sm text-secondary">{t('access.empty')}</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            ) : (
              <p className="text-sm text-secondary">{t('access.loading')}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUS_VARIANT: Record<string, string> = {
  PUBLISHED: 'published',
  COMPLETED: 'completed',
  DRAFT: 'draft',
  CANCELLED: 'cancelled',
  UPCOMING: 'upcoming',
  FINISHED: 'finished',
  UNPUBLISHED: 'draft',
  IN_PROGRESS: 'completed',
};

const STATUS_KEY: Record<string, string> = {
  PUBLISHED: 'sessionPage.status.published',
  COMPLETED: 'sessionPage.status.completed',
  DRAFT: 'sessionPage.status.draft',
  CANCELLED: 'sessionPage.status.cancelled',
  UPCOMING: 'sessionPage.status.upcoming',
  FINISHED: 'sessionPage.status.finished',
  UNPUBLISHED: 'sessionPage.status.unpublished',
  IN_PROGRESS: 'sessionPage.status.inProgress',
};

export function useColumns(): ColumnDef<WebinarListItem>[] {
  const { t } = useTranslation('webinars');

  return [
    {
      accessorKey: 'title',
      header: t('sessionsTable.columns.title'),
      cell: ({ row }: { row: Row<WebinarListItem> }) => (
        <div className=" font-medium text-sm flex flex-col">
          <span>{row.original.title}</span>
          <Badge variant={row.original.type === 'LIVE' ? 'live' : 'evergreen'}>
            {row.original.type}
          </Badge>
        </div>
      ),
    },
    {
      accessorKey: 'date',
      header: t('sessionsTable.columns.date'),
      cell: ({ row }: { row: Row<WebinarListItem> }) => (
        <div className=" font-normal text-sm text-secondary">{row.original.date}</div>
      ),
    },
    {
      accessorKey: 'attendee',
      header: t('sessionsTable.columns.attendees'),
      cell: ({ row }: { row: Row<WebinarListItem> }) => (
        <div className=" font-medium text-sm text-black">{row.original.attendee}</div>
      ),
    },
    {
      accessorKey: 'moderator',
      header: t('sessionsTable.columns.moderators'),
      cell: ({ row }: { row: Row<WebinarListItem> }) => (
        <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
          {row.original.moderator.map((moderator) => (
            <Avatar key={moderator.fullname}>
              <AvatarFallback>{moderator.fullname.charAt(0)}</AvatarFallback>
            </Avatar>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: t('sessionsTable.columns.status'),
      cell: ({ row }: { row: Row<WebinarListItem> }) => (
        <div className=" space-x-2 font-normal text-sm text-secondary">
          {row.original.status && (
            <Badge
              key={row.original.status}
              variant={
                (STATUS_VARIANT[row.original.status] ?? 'incomplete') as Parameters<
                  typeof Badge
                >[0]['variant']
              }
            >
              {t(STATUS_KEY[row.original.status] ?? 'sessionPage.status.upcoming')}
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'acces',
      header: t('sessionsTable.columns.access'),
      cell: ({ row }: { row: Row<WebinarListItem> }) => <AccessCell row={row} />,
    },
  ];
}

{
  /* <Avatar>
          <AvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
          <AvatarFallback>CN</AvatarFallback>
        </Avatar>
        <Avatar>
          <AvatarImage src="https://github.com/maxleiter.png" alt="@maxleiter" />
          <AvatarFallback>LR</AvatarFallback>
        </Avatar>
        <Avatar>
          <AvatarImage src="https://github.com/evilrabbit.png" alt="@evilrabbit" />
          <AvatarFallback>ER</AvatarFallback>
        </Avatar> */
}
